import React from "react"
import * as A from "fp-ts/Array"
import * as O from "fp-ts/Option"
import * as S from "fp-ts/string"
import { Icon } from "../../../components"
import { pipe } from "fp-ts/lib/function"
import * as M from "../../Model/overviewModel"
import {
  agentName,
  numberAbreviation,
  percentNumber,
} from "../../../lib/format"

type Props = {
  valuations: M.AgentValuation[]
  index: number
  updateModalContents: (contents: M.ValuationDetail) => void
}

export default function ValuationDetail({
  valuations,
  index,
  updateModalContents,
}: Props): JSX.Element {
  const carouselRef = React.useRef<HTMLDivElement>(null)
  const leftRef = React.useRef<HTMLButtonElement>(null)
  const rightRef = React.useRef<HTMLButtonElement>(null)

  const scrollToIndex = (step: number) => {
    if (step < 0) return
    if (step >= valuations.length) return
    updateModalContents({ valuations, index: step })
  }

  React.useEffect(() => {
    if (index === null || !carouselRef.current) return

    const selectedChild = carouselRef.current.children[index]

    if (!selectedChild) return

    selectedChild.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    })

    if (leftRef.current) leftRef.current.disabled = index === 0
    if (rightRef.current)
      rightRef.current.disabled = index === valuations.length - 1
    scrollToIndex(index)
  }, [index])

  return pipe(
    valuations[index],
    O.fromNullable,
    O.match(
      () => <></>,
      selectedValuation => (
        <div className="w-full" data-cy={`dashboard-valuation-detail-${index}`}>
          <h3>Your Valuations</h3>

          <div className="relative">
            <div
              ref={carouselRef}
              className={
                "mb-10 mt-4 carousel w-[calc(100%+48px)] py-6" +
                " gap-[12px] -translate-x-6 [--set-d:calc(50%-90px)] "
              }
            >
              {pipe(
                valuations,
                A.mapWithIndex((idx, { valuation, agent }) => {
                  return (
                    <button
                      key={idx}
                      data-cy={`dashboard-valuation-detail-card-${idx}`}
                      className={
                        index === idx
                          ? "scale-125 origin-center shadow-[0_0_15px_10px_rgba(255,255,255,0.3)] z-10 selected outline-none"
                          : ""
                      }
                      onClick={() =>
                        updateModalContents({ valuations, index: idx })
                      }
                      autoFocus={index === idx}
                    >
                      <div className="card p-[25px] w-[210px] h-[180px] flex-[0_0_auto] bg-slate focus-visible:outline-brick text-white text-left flex flex-col ">
                        <div className="grow">
                          <h5 className="heading text-white text-[2.9rem] leading-none tracking-[0.05em]">
                            <FormattedPrice value={valuation.propertyValue} />
                          </h5>
                          <p className="mt-[18px] text-cutoff">
                            {agentName({
                              brand: agent.brand,
                              branch: agent.branch,
                            })}
                          </p>
                        </div>
                        {pipe(
                          valuation.comments,
                          O.match(
                            () => <></>,
                            comments =>
                              S.isEmpty(comments) ? (
                                <></>
                              ) : (
                                <p className="tag px-2">Comments</p>
                              )
                          )
                        )}
                      </div>
                    </button>
                  )
                })
              )}
            </div>
            <div>
              <button
                ref={leftRef}
                data-cy={`dashboard-valuation-detail-nav-left`}
                className="w-8 h-8 rounded-full -scale-x-100 absolute left-2 top-1/2  -translate-y-1/2 shadow-[-2px_2px_6px_rgba(0,0,0,0.3)] 
               [--icon-fill:white] [--icon-stroke:black] disabled:[--icon-fill:#eee] disabled:opacity-50"
                onClick={() => scrollToIndex(index - 1)}
                aria-label="scroll carousel left"
              >
                <Icon.ChevronRight />
              </button>
              <button
                ref={rightRef}
                data-cy={`dashboard-valuation-detail-nav-right`}
                className={
                  " w-8 h-8 rounded-full " +
                  "absolute top-1/2 right-2 -translate-y-1/2 shadow-[-2px_2px_6px_rgba(0,0,0,0.3)] " +
                  " [--icon-fill:white] [--icon-stroke:black] disabled:[--icon-fill:#eee] disabled:opacity-50 "
                }
                onClick={() => scrollToIndex(index + 1)}
                aria-label="scroll carousel right"
              >
                <Icon.ChevronRight />
              </button>
            </div>
          </div>

          <div className="mb-4">
            <h4>{selectedValuation.agent.brand}</h4>
          </div>

          <dl className="mb-10 grid xs:grid-cols-[200px_1fr] gap-x-4 text-lg ">
            {pipe(
              selectedValuation.agent.branch,
              O.match(
                () => <></>,
                branch =>
                  S.isEmpty(branch) ? (
                    <></>
                  ) : (
                    <>
                      <dt>Branch</dt>
                      <dd className="font-mono text-base mb-4 xs:mb-0">
                        {branch}
                      </dd>
                    </>
                  )
              )
            )}
            <dt>Valuation</dt>
            <dd className="font-mono text-base mb-4 xs:mb-0">
              <FormattedPrice
                value={selectedValuation.valuation.propertyValue}
              />
            </dd>
            <dt>Rejection Value</dt>
            <dd className="font-mono text-base mb-4 xs:mb-0">
              {pipe(
                selectedValuation.valuation.rejectionAt,
                O.match(
                  () => <>None</>,
                  rejectionAt => <FormattedPrice value={rejectionAt} />
                )
              )}
            </dd>
            {pipe(
              selectedValuation.agent.segmentation.marketShare,
              O.match(
                () => <></>,
                marketShare => (
                  <>
                    <dt>Market Share</dt>
                    <dd className=" font-mono text-base mb-4 xs:mb-0">
                      {percentNumber(marketShare, 2)} %
                    </dd>
                  </>
                )
              )
            )}
            {pipe(
              selectedValuation.valuation.expectedViewings,
              O.match(
                () => <></>,
                expectedViewings => (
                  <>
                    <dt>Expected Viewings</dt>
                    <dd className=" font-mono text-base mb-4 xs:mb-0">
                      {Math.round(expectedViewings)}
                    </dd>
                  </>
                )
              )
            )}
            <dt>Date</dt>
            <dd className="font-mono text-base mb-4 xs:mb-0">
              {new Date(selectedValuation.valuation.submitted).toDateString()}
            </dd>
            <dt>Comment</dt>
            <dd className="font-mono_ text-base max-w-[500px] min-h-[4lh]">
              {O.match(
                () => "None",
                (cmnt: string) => cmnt
              )(selectedValuation.valuation.comments)}
            </dd>
          </dl>
          <div
            className="hidden md:block"
            data-cy="dashboard-valuation-detail-more-info"
          >
            <h4>Let's take a closer look:</h4>
            <p>Here's a side by side view of all the responses and data</p>
          </div>

          <div
            className="mb-20 hidden md:block"
            data-cy="dashboard-valuation-detail-table"
          >
            <table className="mt-4 w-full">
              <thead>
                <tr className="font-deia text-lg text-left">
                  <th className="px-4">Agent</th>
                  <th className="px-4">Value</th>
                  <th className="px-4">
                    <Popover label="The price at which they might not accept the instruction">
                      Reject
                    </Popover>
                  </th>
                  <th>
                    <Popover label="Marketshare">
                      <span className="whitespace-pre">%Mkt</span>
                    </Popover>
                  </th>
                  <th>
                    <Popover label="Expected viewings">Exp V</Popover>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pipe(
                  valuations,
                  A.mapWithIndex((idx, { valuation, agent }) => {
                    return (
                      <tr
                        key={idx}
                        className={
                          "relative group cursor-pointer hover:bg-tile-light " +
                          (idx === index ? " bg-tile " : " ")
                        }
                        onClick={() =>
                          updateModalContents({ valuations, index: idx })
                        }
                      >
                        <td className="px-4 py-1 flex justify-between gap-4">
                          <span className=" text-cutoff max-w-[260px]">
                            {pipe(
                              agent.branch,
                              O.fold(
                                () => agent.brand,
                                branch =>
                                  branch.length > 0
                                    ? `${agent.brand} - ${branch}`
                                    : agent.brand
                              )
                            )}
                          </span>
                        </td>
                        <td className="px-4 font-mono ">
                          <FormattedPrice value={valuation.propertyValue} />
                        </td>
                        <td className="px-4 font-mono">
                          {O.fold(
                            () => <>N/A</>,
                            (rejectionAt: number) => (
                              <FormattedPrice value={rejectionAt} />
                            )
                          )(valuation.rejectionAt)}
                        </td>
                        <td className="px-4 font-mono">
                          {pipe(
                            agent.segmentation.marketShare,
                            O.match(
                              () => <>N/A</>,
                              marketshare => (
                                <>{percentNumber(marketshare, 2)} %</>
                              )
                            )
                          )}
                        </td>
                        <td className="px-4 font-mono">
                          {pipe(
                            valuation.expectedViewings,
                            O.match(
                              () => <></>,
                              expectedViewings => (
                                <>{Math.round(expectedViewings)}</>
                              )
                            )
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )
    )
  )
}

function FormattedPrice({ value }: { value: number }) {
  const formatted = numberAbreviation(value)

  return (
    <>
      <span className="text-[0.9em]">£ {formatted.value}</span>
      <span className="ml-2 text-[0.8em]">
        {O.getOrElse(() => "")(formatted.name)}
      </span>
    </>
  )
}

function Popover({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="relative group px-4">
      {children}
      <div
        className={
          "absolute top-0 right-2 translate-x-[50%] translate-y-[-50%] w-4 h-4 " +
          " grid place-items-center  bg-lawn " +
          " group-hover:hidden"
        }
      >
        <span className="font-mono leading-none text-[8px]">i</span>
      </div>
      <div className="hidden group-hover:block">
        <p className=" absolute top-2 right-2 translate-x-[19px] -translate-y-full marker bg-lawn w-max max-w-[300px]">
          {label}
        </p>
      </div>
    </div>
  )
}
